import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";

/**
 * HeroAvatar
 * 2D portrait styled to feel like a premium 3D character render.
 * Effects: parallax cursor tilt, breathing scale, gentle float, occasional blink,
 * cyan/violet rim-light halo, glassmorphism backdrop. Respects prefers-reduced-motion.
 */
const HeroAvatar = ({
  src = "/avatar.jpg",
  alt = "Portrait of Soumyajit Raj, Java Backend Developer",
}) => {
  const wrapRef = useRef(null);
  const prefersReduced = useReducedMotion();

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [8, -8]), {
    stiffness: 120,
    damping: 18,
  });
  const ry = useSpring(useTransform(mx, [-1, 1], [-10, 10]), {
    stiffness: 120,
    damping: 18,
  });
  const px = useSpring(useTransform(mx, [-1, 1], [-12, 12]), {
    stiffness: 90,
    damping: 20,
  });
  const py = useSpring(useTransform(my, [-1, 1], [-8, 8]), {
    stiffness: 90,
    damping: 20,
  });

  useEffect(() => {
    if (prefersReduced) return;
    const onMove = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const nx = (e.clientX - cx) / (window.innerWidth / 2);
      const ny = (e.clientY - cy) / (window.innerHeight / 2);
      mx.set(Math.max(-1, Math.min(1, nx)));
      my.set(Math.max(-1, Math.min(1, ny)));
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my, prefersReduced]);

  // Occasional blink — a thin band sweeps across the eye line every 5–8s.
  const [blink, setBlink] = useState(false);
  useEffect(() => {
    if (prefersReduced) return;
    let mounted = true;
    const loop = () => {
      const delay = 4500 + Math.random() * 3500;
      setTimeout(() => {
        if (!mounted) return;
        setBlink(true);
        setTimeout(() => mounted && setBlink(false), 140);
        loop();
      }, delay);
    };
    loop();
    return () => {
      mounted = false;
    };
  }, [prefersReduced]);

  const [loaded, setLoaded] = useState(false);

  return (
    <div
      ref={wrapRef}
      className="relative w-full max-w-[420px] aspect-square mx-auto"
      data-testid="hero-avatar"
      style={{ perspective: 1200 }}
    >
      {/* Ambient halo */}
      <motion.div
        aria-hidden="true"
        className="absolute -inset-10 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,240,255,0.22), rgba(139,92,246,0.12) 55%, transparent 75%)",
          filter: "blur(20px)",
          x: px,
          y: py,
        }}
        animate={
          prefersReduced
            ? {}
            : { scale: [1, 1.05, 1], opacity: [0.85, 1, 0.85] }
        }
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Outer glass ring */}
      <motion.div
        className="absolute inset-0 rounded-full glass"
        animate={prefersReduced ? {} : { y: [0, -3, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "conic-gradient(from 220deg, rgba(0,240,255,0.18), rgba(139,92,246,0.18), rgba(0,240,255,0.18))",
          padding: 1,
        }}
      >
        <div className="w-full h-full rounded-full bg-[#070710] glass" />
      </motion.div>

      {/* Inner avatar disc with tilt + parallax + breathing */}
      <motion.div
        style={{
          rotateX: prefersReduced ? 0 : rx,
          rotateY: prefersReduced ? 0 : ry,
          transformStyle: "preserve-3d",
        }}
        animate={
          prefersReduced
            ? {}
            : { y: [0, -6, 0], scale: [1, 1.012, 1] }
        }
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute inset-3 rounded-full overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        {/* Loading shimmer fallback */}
        {!loaded && (
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 animate-pulse"
          />
        )}

        <img
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          style={{
            // dial color toward the cyan/violet rim palette
            filter:
              "contrast(1.05) saturate(0.92) brightness(0.95) hue-rotate(-6deg)",
          }}
        />

        {/* Cyan rim light from right */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none mix-blend-screen opacity-70"
          style={{
            background:
              "radial-gradient(120% 80% at 95% 25%, rgba(0,240,255,0.45), transparent 45%)",
          }}
        />
        {/* Violet bounce from left */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none mix-blend-screen opacity-50"
          style={{
            background:
              "radial-gradient(120% 80% at 5% 85%, rgba(139,92,246,0.55), transparent 50%)",
          }}
        />
        {/* Vignette to keep face the focal point */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, transparent 55%, rgba(3,3,5,0.55) 100%)",
          }}
        />

        {/* Blink overlay — thin dark band across the eyes */}
        <motion.div
          aria-hidden="true"
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            top: "36%",
            height: "8%",
            background:
              "linear-gradient(180deg, transparent, rgba(0,0,0,0.55), transparent)",
            mixBlendMode: "multiply",
            opacity: blink ? 1 : 0,
            transition: "opacity 90ms ease-out",
          }}
        />
      </motion.div>

      {/* Status dot */}
      <motion.div
        className="absolute bottom-3 right-3 sm:bottom-5 sm:right-5 glass px-3 py-1.5 rounded-full flex items-center gap-2"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        data-testid="avatar-status"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-200">
          Online
        </span>
      </motion.div>

      {/* Subtle floor reflection / glow */}
      <div
        aria-hidden="true"
        className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-20 rounded-[50%]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,240,255,0.25), transparent 70%)",
          filter: "blur(18px)",
        }}
      />
    </div>
  );
};

export default HeroAvatar;
